import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, Settings2, X } from "lucide-react";
import { ASK_QUESTION_MAX, type AskProduct, type AskToolsetBridge, type ToolboxBridge } from "@ttc-spectree/toolbox-contract";
import { Chip } from "@/components/grooming/grooming-chips";
import { AskAnswer, askQuestionBefore } from "@/components/toolbox/ask/ask-answer";
import { AskProductPicker } from "@/components/toolbox/ask/ask-product-picker";
import { HelpAskBox } from "@/components/toolbox/help/help-ask-box";
import { HelpPersonBubble } from "@/components/toolbox/help/help-message";
import { HelpNotReady } from "@/components/toolbox/help/help-not-ready";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { ASK_OPEN_IN_TOOLBOX_REASON } from "@/components/toolbox/require-ask-toolbox";
import { Button } from "@/components/ui/button";
import { useAskProducts } from "@/hooks/queries/use-ask-products";
import { useMe } from "@/hooks/queries/use-me";
import { canAnywhere, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useAskConversation } from "@/hooks/use-ask-conversation";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  askContextFor,
  askNotReadyCopy,
  askReadinessFromFailure,
  askReadinessFromProduct,
  askScopeKey,
  type AskProductReadiness,
} from "@/lib/toolbox/ask";
import { useToolboxBridge, useToolsetSupport } from "@/lib/toolbox/bridge";
import { TOOLBOX_SETUP_PATH } from "@/lib/toolbox/page-names";
import { ASK_TOOL_NAME, TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { SUPPORT_LABELS } from "@/pages/toolbox/home";

/**
 * `/toolbox/ask`: Product Q&A (epic PI-SPEC-SW-E310, feature PI-SPEC-SW-1628,
 * ER-1667 Package F, Resolved Decisions 10 and 10d). Someone picks a product,
 * asks how it behaves, and their own Claude Code reads the product's code on
 * this computer and answers in plain words with one verdict saying whether a
 * written spec backs it.
 *
 * - The product list is the only SpecTree read (`GET /toolbox/ask/products`).
 * - Picking a product checks its code on this computer
 *   (`ask.checkProduct`). A product that needs setting up first shows its
 *   fixed plain sentence and the way to Setup, never the ask box.
 * - The chat reuses Help's ask box, bubbles and not-ready card: up to three
 *   screenshots, streamed answers, Stop and Close conversation. History is
 *   page memory plus the engine's folder on this computer; nothing is posted
 *   to SpecTree, and leaving or closing deletes it.
 *
 * Behind RequireAskToolbox (the `ask` capability somewhere, inside the app).
 * In an app without the ask toolset it shows the same update words as the
 * home tile.
 */

const ASK_FOOTER =
  "Answers use your Claude plan. Product Q&A reads this product's code and written specs on your computer and never changes anything. Nothing is saved in SpecTree.";

function Waiting({ label, testId }: { label: string; testId?: string }) {
  return (
    <span className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid={testId}>
      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {label}
    </span>
  );
}

function PageHeader({ onClose }: { onClose?: (() => void) | undefined }) {
  return (
    <div className="flex flex-wrap items-start gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h1 className={TOOLBOX_PAGE_TITLE}>{ASK_TOOL_NAME}</h1>
        <p className="text-[13px] text-muted-foreground">
          Ask how the product behaves. Your Claude reads the product&apos;s code and answers in plain words, saying
          whether a written spec backs it.
        </p>
      </div>
      {onClose && (
        <Button variant="outline" size="sm" onClick={onClose} data-testid="ask-close-conversation">
          <X aria-hidden="true" />
          Close conversation
        </Button>
      )}
    </div>
  );
}

/** The plain stop when the chosen product needs setting up first: the fixed sentence and the way to Setup. */
function SetUpFirst({ message }: { message: string }) {
  return (
    <div
      className="flex flex-wrap items-start gap-3 rounded-[10px] border border-[rgba(245,158,11,0.4)] bg-[#14110A] px-4 py-3"
      data-testid="ask-set-up-first"
    >
      <p className="flex-1 text-[13px] leading-[1.55] text-[#E4E4E7]">{message}</p>
      <Button asChild variant="outline" size="sm">
        <Link to={TOOLBOX_SETUP_PATH} data-testid="ask-open-setup">
          <Settings2 aria-hidden="true" />
          Open Setup
        </Link>
      </Button>
    </div>
  );
}

function NotNow({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-[10px] border border-[#27272A] bg-[#121215] px-4 py-3"
      data-testid="ask-not-now"
    >
      <p className="flex-1 text-[13px] leading-[1.55] text-[#E4E4E7]">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw aria-hidden="true" />
        Check again
      </Button>
    </div>
  );
}

function AskFlow({ ask, bridge }: { ask: AskToolsetBridge; bridge: ToolboxBridge }) {
  const navigate = useNavigate();
  const products = useAskProducts();
  const { data: access } = useToolboxAccess();
  const me = useMe();
  // FogBugz-linked products are listed only with the person's own FogBugz key (feature PI-SPEC-SW-1630).
  const fogbugzKeyMissing = canAnywhere(access, "fogbugz") && me.data !== undefined && !me.data.hasFogbugzKey;
  const view = useAskConversation(ask);
  const [product, setProduct] = useState<AskProduct | null>(null);
  const [readiness, setReadiness] = useState<AskProductReadiness>({ state: "checking" });
  const checkSeq = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  const check = useCallback(
    (chosen: AskProduct) => {
      const mine = ++checkSeq.current;
      const known = askReadinessFromProduct(chosen);
      if (known) {
        setReadiness(known);
        return;
      }
      setReadiness({ state: "checking" });
      ask
        .checkProduct({ scope: chosen.scope })
        .then((result) => {
          if (mine !== checkSeq.current) return;
          setReadiness(result.ready ? { state: "ready" } : askReadinessFromFailure(result.errorCode, result.message));
        })
        .catch(() => {
          if (mine !== checkSeq.current) return;
          setReadiness({
            state: "not-now",
            errorCode: null,
            message: "This product's code could not be checked on this computer. Try again in a moment.",
          });
        });
    },
    [ask],
  );

  const { closeConversation, messages } = view;
  const choose = useCallback(
    (chosen: AskProduct) => {
      // One product per conversation: a new product starts a new one.
      if (messages.length > 0) closeConversation();
      setProduct(chosen);
      check(chosen);
    },
    [messages.length, closeConversation, check],
  );

  // One product only: choose it for the person.
  const list = products.data;
  useEffect(() => {
    if (product === null && list?.length === 1 && list[0]) choose(list[0]);
  }, [product, list, choose]);

  const messageCount = view.messages.length;
  const lastText = view.messages.at(-1)?.text.length ?? 0;
  useEffect(() => {
    if (messageCount > 0 && typeof bottomRef.current?.scrollIntoView === "function") {
      bottomRef.current.scrollIntoView({ block: "end" });
    }
  }, [messageCount, lastText]);

  const status = view.status;
  const engineReady = status?.state === "ready" || (status === null && view.statusFailed);
  const notReadyCopy = status && status.state !== "ready" ? askNotReadyCopy(status) : null;

  const send = () => {
    if (!product) return;
    void view.ask(product.scope, askContextFor(access, product.scope));
  };

  return (
    <div className="flex w-full max-w-4xl flex-col gap-4 px-4 pb-6 pt-7" data-testid="ask-page">
      <PageHeader onClose={messageCount > 0 ? closeConversation : undefined} />

      {products.isLoading ? (
        <Waiting label="Loading your products" testId="ask-products-loading" />
      ) : products.isError ? (
        <div role="alert" className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400" data-testid="ask-products-error">
          Your products could not be loaded. Reload the page to try again.
        </div>
      ) : !list || list.length === 0 ? (
        <p className="text-[13px] text-muted-foreground" data-testid="ask-no-products">
          No products to ask about yet. Ask an Admin to set up the repositories of your project on the Project page.
        </p>
      ) : (
        <AskProductPicker
          products={list}
          selectedKey={product ? askScopeKey(product.scope) : null}
          hasConversation={messageCount > 0}
          onSelect={choose}
          fogbugzKeyMissing={fogbugzKeyMissing}
        />
      )}

      {product && readiness.state === "checking" && (
        <Waiting label="Checking this product's code on this computer" testId="ask-checking" />
      )}
      {product && readiness.state === "set-up-first" && <SetUpFirst message={readiness.message} />}
      {product && readiness.state === "not-now" && (
        <NotNow
          message={readiness.message}
          onRetry={() => {
            check(product);
          }}
        />
      )}

      {product && readiness.state === "ready" && (
        <>
          {status && notReadyCopy && (
            <HelpNotReady
              status={status}
              bridge={bridge}
              copy={notReadyCopy}
              signInOpenedLine="Claude's sign-in opened in your browser. Product Q&A is ready once you are signed in."
              onOpenSetup={() => {
                void navigate(TOOLBOX_SETUP_PATH);
              }}
              onSignedIn={view.recheckStatus}
            />
          )}
          <section className="flex flex-col gap-4" aria-label="Conversation" data-testid="ask-conversation">
            {messageCount === 0 ? (
              <p className={cn("text-[13px]", TOOLBOX_TEXT.faint)} data-testid="ask-empty">
                Ask about {product.displayName}: how a screen works, what a setting does, or whether something is
                supposed to happen. Paste a screenshot to show what you see.
              </p>
            ) : (
              view.messages.map((message, index) =>
                message.role === "person" ? (
                  <HelpPersonBubble key={message.id} message={message} />
                ) : (
                  <AskAnswer
                    key={message.id}
                    message={message}
                    codeFoldOpenByDefault={product.codeFoldOpen}
                    question={askQuestionBefore(view.messages, index)}
                    scope={product.scope}
                  />
                ),
              )
            )}
            <div ref={bottomRef} />
          </section>
          <HelpAskBox
            draft={view.draft}
            onDraftChange={view.setDraft}
            attachments={view.attachments}
            attachError={view.attachError}
            asking={view.asking}
            disabledReason={
              notReadyCopy?.askReason ?? (engineReady ? undefined : "Checking Claude on this computer…")
            }
            onSend={send}
            onStop={view.stop}
            onAddFiles={(files) => void view.addImageFiles(files)}
            onRemove={view.removeAttachment}
            placeholder={`Ask about ${product.displayName}, or paste a screenshot…`}
            ariaLabel="Ask about the product"
            maxLength={ASK_QUESTION_MAX}
          />
          <p className={cn("text-xs", TOOLBOX_TEXT.faint)}>{notReadyCopy?.footer ?? ASK_FOOTER}</p>
        </>
      )}
    </div>
  );
}

/** `/toolbox/ask`. */
export function ToolboxAskPage() {
  usePageTitle(ASK_TOOL_NAME);
  const bridge = useToolboxBridge();
  const support = useToolsetSupport("ask");
  const ask = bridge?.ask ?? null;

  if (!bridge) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <OpenInToolboxCallout reason={ASK_OPEN_IN_TOOLBOX_REASON} />
      </div>
    );
  }

  if (support === undefined) {
    return (
      <div className="flex w-full px-4 pt-7">
        <Waiting label="Checking SpecTree Toolbox" />
      </div>
    );
  }

  if (support !== "supported" || !ask) {
    const label = SUPPORT_LABELS[support === "spectree-update-needed" ? "spectree-update-needed" : "app-update-needed"];
    return (
      <div className="flex w-full flex-col gap-3 px-4 pb-6 pt-7" data-testid="ask-update-needed">
        <h1 className={TOOLBOX_PAGE_TITLE}>{ASK_TOOL_NAME}</h1>
        <div>
          <Chip tone={label.tone}>{label.text}</Chip>
        </div>
        <Link to={TOOLBOX_HOME_PATH} className="text-[13px] text-link hover:underline">
          Back to SpecTree Toolbox
        </Link>
      </div>
    );
  }

  return <AskFlow ask={ask} bridge={bridge} />;
}
